import { Component, inject, Input, OnInit } from '@angular/core';
import { NgbNavChangeEvent } from '@ng-bootstrap/ng-bootstrap';
import { map, Observable, Subscription } from 'rxjs';
import { AppGlobalControlValues } from 'src/app/common/app.global.control.values';
import { unsubscribeAll } from 'src/app/common/app.helpers';
import { IDeactivate } from 'src/app/common/guards/save-data-guard/save-data.interface';
import { OperationInfoService } from 'src/app/common/long-operations/operation-info.service';
import ProjectLinks from 'src/app/common/project.links';

import { SocialInvolvementContext } from 'src/app/components/shared/counters/involvement-counters-context.abstract';
import { initialPaginationState } from 'src/app/components/shared/social-initiative/social-initiative.helpers';
import { CellActionTypes } from 'src/app/components/tables/components/cell-actions.type';
import { filterDataForUserTable } from 'src/app/components/tables/table.helpers';
import { actionButtonTestId } from 'src/app/components/user-relation-requests/components/manage-relation-request/manage-relation-request.helper';
import { FormConstructorContent } from 'src/app/interfaces/form-and-field.interface';
import { PaginationResolver } from 'src/app/interfaces/pagination.interface';
import {
  TableCellOnActionEvent,
  TableConstructorData,
  UserTableConstructorInfo,
} from 'src/app/interfaces/tables.interfaces';
import { DialogActionTypes } from 'src/app/notifications/dialogs/dialogs.enum';
import { NotificationContextAttributesService } from 'src/app/notifications/notifications-context-attributes.service';
import {
  PaginatablePendingUserInvolvements,
  Project,
  RequestCountersBasic,
  UpdateProjectRequest,
} from 'src/app/swagger-api';
import { ManageProjectService } from '../../manage-project.service';

const TabType = {
  USER_REQUESTS_JOIN: 'userRequestsJoin',
  PENDINGS_INVITATIONS: 'pendingInvitations',
};

@Component({
  selector: 'app-manage-project',
  templateUrl: './manage-project.component.html',
  styleUrls: ['./manage-project.component.scss'],
  providers: [ManageProjectService],
})
export class ManageProjectComponent extends SocialInvolvementContext implements OnInit, IDeactivate {
  @Input({ required: true }) project: Project;

  public readonly LINK_PROJECTS_VIEW = ProjectLinks.LINK_PROJECTS_VIEW;

  public currentTab = TabType.USER_REQUESTS_JOIN;

  private subscriptions$: Subscription[] = [];

  private tabTypes: { [key: string]: () => void } = {
    userRequestsJoin: () =>
      this.manageProjectService.fetchUsersRequestsForEntity(
        this.project.entity_id,
        this.calculateOffset(this.resolver),
        this.pageSize,
        this.sorting,
        this.project.permissions.accept_join_request,
      ),
    pendingInvitations: () =>
      this.manageProjectService.fetchUsersInvitationsForEntity(
        this.project.entity_id,
        this.calculateOffset(this.resolver),
        this.pageSize,
        this.sorting,
        this.project.permissions.invite_users,
      ),
  };

  private manageProjectService = inject(ManageProjectService);
  private operationInfoService = inject(OperationInfoService);
  private modalAttributesService = inject(NotificationContextAttributesService);

  public readonly formContent$: Observable<FormConstructorContent> = this.manageProjectService.onFormAttrChanges;
  public readonly tableData$: Observable<UserTableConstructorInfo> = this.manageProjectService.tableDataChanged.pipe(
    map((data: TableConstructorData) => filterDataForUserTable(data)),
  );

  public ngOnInit(): void {
    this.addSubscriptions();
    this.initializeResolvers();

    this.manageProjectService.fetchAttributeMetadata(this.project, true);
    this.manageProjectService.fetchRequestCounters(this.project.entity_id);
  }

  public setFormStatus(dirty: boolean): void {
    this.isFormChanged = dirty;
  }

  public ngOnDestroy(): void {
    unsubscribeAll(this.subscriptions$);
  }

  public onTabClick($event: NgbNavChangeEvent<string>): void {
    this.resetSortingParameters();
    this.resetPaginationControls();
    this.manageProjectService.fetchRequestCounters(this.project.entity_id);

    if (this.tabTypes[$event.nextId]) {
      this.currentTab = $event.nextId;
      this.tabTypes[this.currentTab]();
    }
  }

  public onTableAction(event: TableCellOnActionEvent): void {
    const actions: { [key: string]: () => void } = {
      [CellActionTypes.CONFIRM]: () => this.onConfirmRequestOrInvite(event),
      [CellActionTypes.REJECT]: () => this.onRejectRequestOrInvite(event),
      [CellActionTypes.REMOVE]: () => this.onRemoveInvite(event),
    };
    actions[event.type] && actions[event.type]();
  }

  public onConfirmRequestOrInvite(event: TableCellOnActionEvent): void {
    this.operationInfoService.setupOperation(
      {
        spinner: 'button',
        button: { id: actionButtonTestId(CellActionTypes.CONFIRM, event) },
        successfulMessage: 'MANAGE_PROJECT_CONFIRM_USER_REQUEST_HINT',
        blockedUIControls: [
          { elementId: AppGlobalControlValues.LOG_OUT_BUTTON },
          { elementId: actionButtonTestId(CellActionTypes.REJECT, event) },
        ],
      },
      () => {
        this.manageProjectService.onConfirmRequestOrInvite(event).subscribe(() => {
          this.onListUpdate();
        });
      },
    );
  }

  public onRemoveInvite(event: TableCellOnActionEvent): void {
    this.operationInfoService.setupOperation(
      {
        blockedUIControls: [{ elementId: AppGlobalControlValues.LOG_OUT_BUTTON }],
        confirmationDialog: {
          description: 'MANAGE_ENTITY_REMOVE_REQUEST_DIALOG_DESCRIPTION',
          title: 'MANAGE_ENTITY_REMOVE_REQUEST_DIALOG_TITLE',
          buttons: [
            this.modalAttributesService.createDialogButton('BTN_CLOSE_HINT'),
            this.modalAttributesService.createDialogButton(
              'MANAGE_ENTITY_REMOVE_REQUEST_DIALOG_BTN_HINT',
              'btn-primary',
              DialogActionTypes.CONFIRM_ACTION,
            ),
          ],
        },
        successfulMessage: 'MANAGE_ENTITY_REMOVE_USER_REQUEST_HINT',
      },
      () => {
        this.manageProjectService.onRemoveInvite(event).subscribe(() => {
          this.onListUpdate();
        });
      },
    );
  }

  public onRejectRequestOrInvite(event: TableCellOnActionEvent): void {
    this.operationInfoService.setupOperation(
      {
        blockedUIControls: [
          { elementId: AppGlobalControlValues.LOG_OUT_BUTTON },
          { elementId: actionButtonTestId(CellActionTypes.CONFIRM, event) },
        ],
        confirmationDialog: {
          description: 'MANAGE_COMMUNITY_REJECT_REQUEST_DIALOG_DESCRIPTION',
          title: 'MANAGE_COMMUNITY_REJECT_REQUEST_DIALOG_TITLE',
          buttons: [
            this.modalAttributesService.createDialogButton('BTN_CLOSE_HINT'),
            this.modalAttributesService.createDialogButton(
              'MANAGE_COMMUNITY_REJECT_REQUEST_DIALOG_BTN_HINT',
              'btn-primary',
              DialogActionTypes.CONFIRM_ACTION,
            ),
          ],
        },
        successfulMessage: 'MANAGE_COMMUNITY_REJECT_USER_REQUEST_HINT',
      },
      () => {
        this.manageProjectService.onRejectRequestOrInvite(event).subscribe(() => {
          this.onListUpdate();
        });
      },
    );
  }

  public onRemoveProject(projectId: string): void {
    this.operationInfoService.setupOperation(
      {
        confirmationDialog: {
          description: 'PROJECTS_REMOVE_PROJECT_DESCRIPTION',
          title: 'PROJECTS_REMOVE_PROJECT_TITLE',
          buttons: [
            this.modalAttributesService.createDialogButton('BTN_CLOSE_HINT'),
            this.modalAttributesService.createDialogButton(
              'PROJECTS_REMOVE_PROJECT_BTN_HINT',
              'btn-primary',
              DialogActionTypes.CONFIRM_ACTION,
            ),
          ],
        },

        successfulMessage: 'MANAGE_PROJECT_REMOVE_HINT',
      },
      () => {
        this.manageProjectService.removeProject(projectId);
      },
    );
  }

  public onUpdateProject(project: UpdateProjectRequest): void {
    this.isFormChanged = false;
    this.operationInfoService.setupOperation(
      {
        spinner: 'global',
        button: { id: 'btn-manage-project' },
        successfulMessage: 'MANAGE_PROJECT_UPDATE_DESCRIPTION',
      },
      () => {
        this.manageProjectService.updateEntity(project, this.project.entity_id);
      },
    );
  }

  protected subscribeOnCounterChanges(): void {
    this.subscriptions$.push(
      this.manageProjectService.projectsInvolvementCountersChanges.subscribe((counters: RequestCountersBasic) => {
        this.receivedCounter = counters.requests_received;
        this.sentCounter = counters.requests_sent;
      }),
    );
  }

  protected onListUpdate(): void {
    if (this.resolver.state.pagination.itemsCount === 1 && this.resolver.state.pagination.currentPage > 1) {
      this.resolver.state.pagination.currentPage -= 1;
    }

    this.manageProjectService.fetchRequestCounters(this.project.entity_id);
    this.tabTypes[this.currentTab]();
  }

  private addSubscriptions(): void {
    this.subscribeOnCounterChanges();
    this.subscribeOnResolverChanges();
  }

  private subscribeOnResolverChanges(): void {
    this.subscriptions$.push(
      this.manageProjectService.resolverChanged.subscribe((data: PaginatablePendingUserInvolvements) => {
        this.resolver.state.pagination.totalItemsCount = data.total_count;
        this.resolver.state.pagination.itemsCount = data.items.length;
      }),
    );
  }

  private resetPaginationControls(): void {
    this.resolver.state = initialPaginationState();
  }

  private calculateOffset(resolver: PaginationResolver): number {
    return (resolver.state.pagination.currentPage - 1) * this.pageSize;
  }

  private initializeResolvers(): void {
    const onPageChangesFactory = (resolver: PaginationResolver) => (page: number) => {
      resolver.state.pagination.currentPage = page;
      this.tabTypes[this.currentTab]();
    };

    this.resolver.onPageChanged = onPageChangesFactory(this.resolver);
  }
}
